<template>
    <div class="h-full w-full flex flex-col">
        <div class="flex justify-between flex-wrap mb-4">
            <h1 class="text-base md:text-lg font-semibold">
                Security Clearance List
            </h1>
            <div class="flex justify-start items-center flex-wrap">
                <div class="flex justify-start items-center flex-wrap">
                    <div class="w-full sm:w-auto mb-4 border rounded-lg px-4 h-8 flex items-center">
                        <div class="text-sm mr-2">Entity:</div>
                        <select v-model="filterByEntity" class="outline-none text-sm flex-grow">
                            <option value="">All</option>
                            <option :value="item.entity" v-for="(item, index) in filteredEntities" :key="index">{{
                                getEntityName(item.entity) }}</option>
                        </select>
                    </div>
                    <div class="w-full sm:w-auto ml-0 sm:ml-8 mb-4">
                        <search @on-search="onSearch" class-list="px-4 h-8"></search>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col" v-if="$apollo.queries.security_clearances.loading">
            <table-shimmer :titleHeaders="[
                '#',
                'Name',
                'Entity',
                'Application Type',
                'Purpose of Request',
                'Submitted At',
                'Status',
                'Actions',
            ]" :rows="first"></table-shimmer>
        </div>
        <div v-else-if="
            !$apollo.queries.security_clearances.loading && !hasItems
        " class="h-full flex justify-center items-center">
            <h1 class="text-center text-xl font-medium">
                No Security Clearance Applications found!
            </h1>
        </div>
        <div v-else class="flex flex-col">
            <div class="flex flex-col overflow-x-auto">
                <div class="inline-block min-w-full py-2">
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-left text-sm font-light">
                            <thead class="border-b font-medium dark:border-neutral-500">
                                <tr>
                                    <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                                        #
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Name
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Entity
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Application Type
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Purpose of Request
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Submitted At
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Status
                                    </th>
                                    <th scope="col" class="px-6 py-2 text-gray-400">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in items" :key="index"
                                    class="border-b border-dashed dark:border-neutral-500">
                                    <td class="whitespace-nowrap px-6 py-2 font-medium">
                                        {{ paginatorInfo.firstItem + index }}
                                    </td>
                                    <td class="whitespace-nowrap px-6 py-2">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 mr-3">
                                                <img class="h-12 w-12 rounded-lg object-cover"
                                                    :src="getSrc(item.profile_picture_url)" alt="Profile Picture" />
                                            </div>
                                            <div class="flex-grow flex flex-col mr-4">
                                                <h1 class="text-sm font-semibold flex items-center">
                                                    <span
                                                        class="text-heading hover:text-blue-600 hover:underline cursor-pointer">{{
                                                            item.fullname }}</span>
                                                </h1>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-2">
                                        {{ getEntityName(item.entity) }}
                                    </td>
                                    <td class="px-6 py-2">{{ item.application_type }}</td>
                                    <td class="px-6 py-2">{{ item.purpose_of_request }}</td>
                                    <td class="whitespace-nowrap px-6 py-2">
                                        {{ getFormattedDate(item.created_at) }}
                                    </td>
                                    <td class="px-6 py-2" v-html="getStatus(item.status, item.note)"></td>
                                    <td class="px-6 py-2">
                                        <div class="flex">
                                            <div v-if="hasPermission('security-clearance-show')"
                                                @click.stop="viewItem(item)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                                <icon-eye
                                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                            </div>
                                            <div v-if="hasPermission('security-clearance-print')"
                                                @click.stop="generatePdf(item.token)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer">
                                                <icon-printer
                                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-printer>
                                            </div>
                                            <div v-if="
                                                hasSuperPermission('security-clearance-approve') &&
                                                isShowApproveReject(item.status)
                                            " @click.stop="approveItem(item.token)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer ml-2">
                                                <icon-approve
                                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-approve>
                                            </div>
                                            <div v-if="
                                                hasSuperPermission('security-clearance-reject') &&
                                                isShowApproveReject(item.status)
                                            " @click.stop="rejectItem(item.token)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer ml-2">
                                                <icon-reject
                                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-reject>
                                            </div>
                                            <div v-if="hasSuperPermission('security-clearance-delete')"
                                                @click.stop="deleteSecurityClearance(index)"
                                                class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer ml-2">
                                                <icon-trash
                                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-trash>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="mt-4">
                <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-modal v-if="showAddNote" :title="`Add Your Note`" @close="closeAddNote">
                <div class="flex flex-col">
                    <div class="flex flex-col" :class="{ invalid: !note.isValid }">
                        <label for="note" class="px-8 mt-4 text-sm ml-1">Note</label>
                        <div class="h-32 px-8 rounded-xl mt-2">
                            <textarea v-model.trim="note.val" @blur="clearValidity('note')" id="note"
                                :class="note.isValid ? '' : 'border-red-600'"
                                class="bg-white resize-none w-full h-full outline-none text-sm border p-4 rounded-lg"></textarea>
                        </div>
                        <transition name="form-fade" mode="out-in">
                            <p v-if="!note.isValid" class="px-8 h-4 text-xs text-red-600 ml-1">
                                {{ note.msg }}
                            </p>
                        </transition>
                    </div>
                    <div class="flex justify-end mt-4 mr-8">
                        <div @click.stop="addNoteOk"
                            class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
                            OK
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem" :title="`View Security Clearance - ${selectedItem.fullname}`"
                @close="closeModal">
                <view-security-clearance class="text-left" :item="selectedItem" :iframe-height="getIframeHeight"
                    @close="closeModal" @approve-item="approveItem"
                    @reject-item="approveOrRejectItem"></view-security-clearance>
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import TableShimmer from "../../../components/shared/TableShimmer.vue";
import Pagination from "../../../components/shared/pagination.vue";
import ViewSecurityClearance from "../view.vue";
import IconEye from "../../../components/icons/IconEye.vue";
import IconApprove from "../../../components/icons/IconApprove.vue";
import IconReject from "../../../components/icons/IconReject.vue";
import IconPrinter from "../../../components/icons/IconPrinter.vue";
import IconTrash from "../../../components/icons/IconTrash.vue";
import { formattedDate } from "../../../helpers/utils";
import Search from "../../../components/shared/search.vue";

export default {
    name: "SecurityClearanceList",
    components: {
        TableShimmer,
        Pagination,
        ViewSecurityClearance,
        IconEye,
        IconApprove,
        IconReject,
        IconPrinter,
        IconTrash,
        Search,
    },
    data() {
        return {
            security_clearances: [],
            first: 10,
            page: 1,
            filterByEntity: "",
            entities: [],
            selectedItem: null,
            isViewItem: false,
            note: {
                val: "",
                isValid: true,
                msg: "",
            },
            showAddNote: false,
            selectedToken: null,
            searchText: "",
        };
    },
    watch: {
        security_clearances(newVal) {
            if (newVal.error) {
                if (newVal.error_code == 403) {
                    this.$router.replace("/no-permission");
                } else if (newVal.error_code == 401) {
                    this.$router.replace("/");
                } else {
                    alert(newVal.message);
                    this.$router.replace("/");
                }
            }
        },
        filterByEntity() {
            this.page = 1;
        },
    },
    computed: {
        items() {
            return this.security_clearances.data;
        },
        hasItems() {
            if (this.items && this.items.length > 0) {
                return true;
            } else {
                return false;
            }
        },
        paginatorInfo() {
            return this.security_clearances.paginatorInfo;
        },
        role() {
            return this.$store.getters.role;
        },
        filteredEntities() {
            return this.entities.data;
        },
        getIframeHeight() {
            return `${window.innerHeight} px`;
        },
    },
    apollo: {
        security_clearances() {
            return {
                query: gql`
          query GetSecurityClearance(
            $first: Int!
            $page: Int!
            $entity: String
            $token: String
            $search: String
          ) {
            security_clearances(
              first: $first
              page: $page
              entity: $entity
              token: $token
              search: $search
            ) {
              error
              error_code
              message
              data {
                token
                fullname
                profile_picture_url
                entity
                application_type
                purpose_of_request
                status
                note
                created_at
              }
              paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
              }
            }
          }
        `,
                variables() {
                    return {
                        first: this.first,
                        page: this.page,
                        entity: this.filterByEntity,
                        token: "",
                        search: this.searchText,
                    };
                },
                fetchPolicy: "cache-and-network",
            };
        },
        entities() {
            return {
                query: gql`
          query GetEntityList {
            entities {
              data {
                id
                entity
              }
            }
          }
        `,
                fetchPolicy: "cache-and-network",
            };
        },
    },
    methods: {
        onSearch(searchText) {
            this.searchText = searchText;
        },
        hasPermission(crudName) {
            if (this.role == "superadmin") {
                return true;
            } else {
                const permissions = this.$store.getters.permissions;
                const permissionsArray = permissions.split(",");
                if (permissionsArray.includes(crudName)) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        hasSuperPermission(crudName) {
            const permissions = this.$store.getters.permissions;
            const permissionsArray = permissions.split(",");
            if (permissionsArray.includes(crudName)) {
                return true;
            } else {
                return false;
            }
        },
        setPage(page) {
            this.page = page;
        },
        getSrc(url) {
            if (url) {
                return process.env.VUE_APP_PUBLIC_URL + url;
            } else {
                return process.env.VUE_APP_PUBLIC_URL + "/storage/default/avatar.jpg";
            }
        },
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        viewItem(item) {
            this.selectedItem = item;
            this.isViewItem = true;
        },
        getFormattedDate(date) {
            return formattedDate(date);
        },
        closeModal() {
            this.isViewItem = false;
            this.selectedItem = null;
        },
        getStatus(status, remarks) {
            if (status === null) {
                return `<div class="text-yellow-600">Under Review</div>`;
            } else if (status === true) {
                return `<div class="text-green-600">Approved</div>`;
            } else {
                return `<div class="flex flex-col"><div class="text-red-600">Rejected</div><div>Remarks: ${remarks}</div></div>`;
            }
        },
        isShowApproveReject(status) {
            if (
                this.role == "admin" ||
                this.role == "superadmin" ||
                this.role == "Admin Support"
            ) {
                if (status === null) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        },
        approveItem(token) {
            this.approveOrRejectItem(token, true, this.note.val);
        },
        rejectItem(token) {
            this.selectedToken = token;
            this.showAddNote = true;
        },
        closeAddNote() {
            this.showAddNote = false;
            this.note = {
                val: "",
                isValid: true,
                msg: "",
            };
            this.selectedToken = null;
        },
        addNoteOk() {
            if (this.note.val === "") {
                this.note.isValid = false;
                this.note.msg = "You must enter a note.";
            } else {
                this.showAddNote = false;
                this.approveOrRejectItem(this.selectedToken, false, this.note.val);
            }
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        async approveOrRejectItem(token, status, note) {
            this.$emit('approve-or-reject', { token, status, note });
        },
        async generatePdf(token) {
            this.$emit('generate-pdf', token);
        },
        async deleteSecurityClearance(index) {
            const item = this.items[index];
            this.$emit('delete-security-clearance', { token: item.token, fullname: item.fullname });
        },
        refresh() {
            this.$apollo.queries.security_clearances.refetch();
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.form-fade-enter-active,
.form-fade-leave-active {
    transition: all 0.5s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
    opacity: 0;
    height: 0px;
}
</style>
